import { useQueryClient } from "@tanstack/react-query";
import { PUBLIC_TOOL_ALLOWLIST } from "@loumy/tools";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { useSession } from "../auth/session";
import { relativeTime } from "../lib/format";
import { useApiKeys, useRevokeApiKey } from "../state/queries";
import { qk } from "../state/queryClient";
import { useUiStore } from "../state/uiStore";
import { Badge, Button, Card, Field, Icon, Segmented, TextInput } from "../ui";

// §8/§9.16/D160/D163 — Screen „MCP & API". Bündelt (A) die API-Key-Verwaltung (verschoben aus
// der Account-Seite) und (B) die Installations-Anleitung für den Loumy-MCP/das Plugin samt
// kurzer Übersicht, was über API/MCP möglich ist. Rein Anzeige + bestehende Key-Mutationen —
// keine neue Server-Mechanik. Der gemintete Klartext-Key bleibt (wie in der Account-Card, Codex-P1)
// ausschließlich im lokalen `minted`-State und wird NUR in die hier gezeigten Befehle gespiegelt —
// nie persistiert (Query-/Mutation-Cache).

// SSOT-Spiegel der Gateway-Basis-URL: identisch zu `DEFAULT_API_BASE` in
// packages/mcp-local/src/bin.mts und der `LOUMY_API_BASE`-Default in plugin/.mcp.json.
const GATEWAY_BASE = "https://loumy-public-gateway.krachertim.workers.dev";
const MCP_ENDPOINT = `${GATEWAY_BASE}/mcp`;
// ASCII + selbstdokumentierend: gerät der Platzhalter unverändert in einen Befehl, ergibt er ein
// klares 401 statt eines kryptischen „invalid header value" (ein Unicode-„…" wäre ein ungültiger
// HTTP-Header-Wert) — und „DEIN_KEY" sagt dem Nutzer, dass er ihn ersetzen muss.
const PLACEHOLDER_KEY = "loco_DEIN_KEY";
// Privates Dist-Repo des Server-Bundles (plugin/README.md). Roadmap public: `npx -y loumy-mcp`.
const NPX_TARGET = "github:TimK2011/loumy-mcp";

// Kurzbeschreibungen der öffentlichen Tools (kuratiert). Die ID-Liste selbst kommt drift-frei aus
// PUBLIC_TOOL_ALLOWLIST (@loumy/tools) — ein neues Public-Tool erscheint hier automatisch, der
// Fallback trägt es auch ohne Eintrag. MCP-Toolname je Eintrag = `generation_<id mit _>`.
const TOOL_BLURBS: Record<string, string> = {
  "image-generator": "Bild aus Text-Prompt und/oder Referenzbild generieren.",
  "chroma-key-remover": "Greenscreen/Farbschlüssel aus Bild oder Video entfernen (transparentes Ergebnis).",
  spritesheet: "Animiertes Spritesheet (Game-Loop) aus einem Motiv erzeugen.",
  "bg-remove": "Hintergrund per KI-Matting freistellen (Bild & Video).",
  "image-upscale": "Bild treu hochskalieren (Real-ESRGAN, 2× / 4×).",
};

// ── kleiner Copy-fähiger Code-/Config-Block ───────────────────────────────────────
function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true); // nur bei ECHTEM Erfolg (kein Erfolg bei Reject)
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard verweigert (z.B. unsicherer Kontext) — Nutzer markiert manuell */
    }
  }

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg border border-border bg-surface-2 p-3 pr-20 font-mono text-xs leading-relaxed text-text">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={() => void onCopy()}
        aria-label="In die Zwischenablage kopieren"
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-medium text-muted hover:text-text"
      >
        {copied ? (
          <>
            <Icon name="check" size={12} className="text-accent" />
            <span className="text-accent">Kopiert</span>
          </>
        ) : (
          "Kopieren"
        )}
      </button>
    </div>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2 text-sm">{children}</div>
    </div>
  );
}

// ── A) API-Keys (verschoben aus der Account-Seite, §9.16) ─────────────────────────
// Der Mint läuft BEWUSST ohne TanStack-Mutation: der Klartext bleibt ausschließlich im an die
// Page gehobenen `minted`-State (Einmal-Anzeige + Snippet-Spiegel), nie im MutationCache.
function ApiKeysCard({ minted, setMinted }: { minted: string | null; setMinted: (v: string | null) => void }) {
  const client = useQueryClient();
  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setCopied(false);
    setCreating(true);
    try {
      const res = await api.createApiKey(name.trim() || undefined);
      setMinted(res.plaintext);
      setName("");
      void client.invalidateQueries({ queryKey: qk.apiKeys });
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : "Key konnte nicht erstellt werden.");
    } finally {
      setCreating(false);
    }
  }

  async function onCopy() {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted);
      setCopied(true); // nur bei ECHTEM Erfolg (Reject darf keinen Erfolg anzeigen)
    } catch {
      setErr("Kopieren fehlgeschlagen — Key bitte manuell markieren.");
    }
  }

  async function onRevoke(id: string) {
    setErr(null);
    try {
      await revoke.mutateAsync(id);
      setConfirmId(null);
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : "Widerruf fehlgeschlagen.");
    }
  }

  return (
    <Card className="space-y-3">
      <form onSubmit={onCreate} className="flex flex-wrap items-end gap-2">
        <div className="min-w-48 flex-1">
          <Field label="Neuer Key" htmlFor="api-key-name">
            <TextInput
              id="api-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional, z. B. CI)"
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              disabled={creating}
            />
          </Field>
        </div>
        <Button type="submit" variant="primary" disabled={creating}>
          {creating ? "…" : "Erstellen"}
        </Button>
      </form>

      {minted && (
        <div className="rounded-lg border border-accent/40 bg-accent/5 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all font-mono text-sm" data-testid="minted-key">
              {minted}
            </code>
            <Button variant="secondary" onClick={() => void onCopy()} title="In die Zwischenablage kopieren">
              {copied ? <Icon name="check" size={14} className="text-accent" /> : "Kopieren"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setMinted(null)}
              title="Anzeige schließen — der Key ist danach nicht wieder abrufbar"
            >
              <Icon name="x" size={14} />
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-amber-400" role="alert">
            Wird nur EINMAL angezeigt — jetzt kopieren. Solange diese Anzeige offen ist, steht der
            Key unten direkt in den Installations-Befehlen.
          </p>
        </div>
      )}

      {err && (
        <p role="alert" className="text-sm text-danger">
          {err}
        </p>
      )}

      {keys.data && keys.data.length > 0 && (
        <ul className="divide-y divide-border">
          {keys.data.map((k) => (
            <li key={k.id} className="flex flex-wrap items-center gap-2 py-2">
              <code className="font-mono text-sm">{k.keyPrefix}…</code>
              <span className="min-w-0 flex-1 truncate text-sm">
                {k.name ?? <span className="text-muted">ohne Namen</span>}
              </span>
              <span
                className="text-xs text-muted"
                title={k.lastUsedAt ? `zuletzt genutzt ${relativeTime(k.lastUsedAt)}` : "noch nie genutzt"}
              >
                {relativeTime(k.createdAt)}
              </span>
              {k.status === "revoked" ? (
                <Badge tone="warn">widerrufen</Badge>
              ) : confirmId === k.id ? (
                <>
                  <Button variant="danger" onClick={() => void onRevoke(k.id)} disabled={revoke.isPending}>
                    Widerrufen?
                  </Button>
                  <Button variant="secondary" onClick={() => setConfirmId(null)}>
                    <Icon name="x" size={14} />
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setConfirmId(k.id)} title="Key widerrufen (zweistufig)">
                  <Icon name="trash" size={14} />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {keys.data && keys.data.length === 0 && !minted && <p className="text-sm text-muted">Noch keine API-Keys.</p>}
    </Card>
  );
}

// ── B) Installation ───────────────────────────────────────────────────────────────
type Client = "claude" | "codex" | "json";

function InstallSection({ keyInput, setKeyInput }: { keyInput: string; setKeyInput: (v: string) => void }) {
  const [client, setClient] = useState<Client>("claude");
  const apiKey = keyInput.trim() || PLACEHOLDER_KEY;
  const hasRealKey = keyInput.trim().length > 0;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Installieren</h2>

      <div className="rounded-lg border border-border bg-surface-2 p-3">
        <Field label="API-Key für die Befehle" htmlFor="install-api-key">
          <TextInput
            id="install-api-key"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={PLACEHOLDER_KEY}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
          />
        </Field>
      </div>

      <Segmented<Client>
        value={client}
        onChange={setClient}
        options={[
          { value: "claude", label: "Claude Code" },
          { value: "codex", label: "Codex" },
          { value: "json", label: "JSON-Config" },
        ]}
      />

      {client === "claude" && (
        <>
          <Card className="space-y-2">
            <p className="font-medium text-text">One-Command-Install</p>
            <CodeBlock
              code={`claude plugin marketplace add https://github.com/TimK2011/loumy-mcp && claude plugin install loumy@loumy${hasRealKey ? ` --config api_key=${apiKey}` : ""}`}
            />
            <p className="text-sm text-muted">
              {hasRealKey
                ? "Marketplace + Plugin + Key in einem"
                : "Marketplace + Plugin — den Key fragt der Install-Dialog ab"}{" "}
              — danach Claude Code neu starten.
            </p>
          </Card>

          <Card className="space-y-5">
            <p className="font-medium text-text">Manuell (Schritt für Schritt)</p>

            <Step n={1}>
              <p className="font-medium text-text">Marketplace hinzufügen</p>
              <CodeBlock code={`claude plugin marketplace add https://github.com/TimK2011/loumy-mcp`} />
            </Step>

            <div className="h-px bg-border" />

            <Step n={2}>
              <p className="font-medium text-text">
                {hasRealKey ? "Plugin installieren — Key inklusive" : "Plugin installieren"}
              </p>
              <CodeBlock
                code={`claude plugin install loumy@loumy${hasRealKey ? ` --config api_key=${apiKey}` : ""}`}
              />
            </Step>
          </Card>

          <Card className="space-y-2">
            <p className="font-medium text-text">Optional: nur MCP-Server (ohne Skills)</p>
            <CodeBlock code={`claude mcp add loumy --env LOUMY_API_KEY=${apiKey} -- npx -y ${NPX_TARGET}`} />
          </Card>
        </>
      )}

      {client === "codex" && (
        <Card className="space-y-5">
          <Step n={1}>
            <p className="font-medium text-text">Per Befehl (stdio)</p>
            <CodeBlock code={`codex mcp add loumy --env LOUMY_API_KEY=${apiKey} -- npx -y ${NPX_TARGET}`} />
          </Step>

          <div className="h-px bg-border" />

          <Step n={2}>
            <p className="font-medium text-text">
              Oder manuell in <code className="font-mono text-xs">~/.codex/config.toml</code>
            </p>
            <CodeBlock
              code={`[mcp_servers.loumy]\ncommand = "npx"\nargs = ["-y", "${NPX_TARGET}"]\nenv = { LOUMY_API_KEY = "${apiKey}", LOUMY_API_BASE = "${GATEWAY_BASE}" }`}
            />
          </Step>
        </Card>
      )}

      {client === "json" && (
        <Card className="space-y-5">
          <Step n={1}>
            <p className="font-medium text-text">Lokaler Server (stdio) — Cursor, Windsurf, Claude Desktop …</p>
            <p className="text-muted">In die MCP-Konfiguration des Clients (z. B. eine `.mcp.json`):</p>
            <CodeBlock
              code={JSON.stringify(
                {
                  mcpServers: {
                    loumy: {
                      command: "npx",
                      args: ["-y", NPX_TARGET],
                      env: { LOUMY_API_KEY: apiKey, LOUMY_API_BASE: GATEWAY_BASE },
                    },
                  },
                },
                null,
                2,
              )}
            />
          </Step>

          <div className="h-px bg-border" />

          <Step n={2}>
            <p className="font-medium text-text">Oder Cloud direkt (HTTP-MCP)</p>
            <p className="text-muted">Für Clients, die HTTP-MCP mit eigenen Headern unterstützen:</p>
            <CodeBlock
              code={JSON.stringify(
                {
                  mcpServers: {
                    "loumy-cloud": {
                      type: "http",
                      url: MCP_ENDPOINT,
                      headers: { "x-api-key": apiKey },
                    },
                  },
                },
                null,
                2,
              )}
            />
          </Step>
        </Card>
      )}
    </section>
  );
}

// ── C) Übersicht der Möglichkeiten ─────────────────────────────────────────────────
function CapabilitiesSection() {
  const tools = [...PUBLIC_TOOL_ALLOWLIST];
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Was damit geht</h2>

      <Card className="space-y-1">
        <p className="mb-2 flex items-center gap-2 text-sm font-medium">
          <Icon name="sparkles" size={16} className="text-accent" />
          Cloud-Tools <span className="text-xs font-normal text-muted">(API-Key nötig)</span>
        </p>
        <ul className="divide-y divide-border">
          {tools.map((id) => (
            <li key={id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-2 text-sm">
              <code className="font-mono text-xs text-accent">{id}</code>
              <span className="min-w-0 flex-1 text-muted">{TOOL_BLURBS[id] ?? "Generierungs-Tool."}</span>
            </li>
          ))}
          <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-2 text-sm">
            <code className="font-mono text-xs text-accent">generation_job_status</code>
            <span className="min-w-0 flex-1 text-muted">Status eines Jobs samt Download-URLs abfragen.</span>
          </li>
        </ul>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="space-y-1">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Icon name="tools" size={16} className="text-accent" />
            Editor-Tools <span className="text-xs font-normal text-muted">(lokal)</span>
          </p>
          <p className="text-sm text-muted">
            Projekt-Setup, Behavior-/Placeable-/Manifest-Generierung — editieren Dateien im lokalen
            Projekt. Immer aktiv (auch ohne Key), nur über den lokalen Server/das Plugin.
          </p>
        </Card>
        <Card className="space-y-1">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Icon name="wand" size={16} className="text-accent" />
            Skills <span className="text-xs font-normal text-muted">(nur Plugin)</span>
          </p>
          <p className="text-sm text-muted">
            Editor-Wissen für den Agenten + Bedienungs-Guide der Loumy-Editor-UI für Anwender —
            ausschließlich im Claude-Code-Plugin enthalten.
          </p>
        </Card>
      </div>
    </section>
  );
}

export function McpApi() {
  const { status } = useSession();
  const openAuth = useUiStore((s) => s.openAuth);
  // Klartext-Keys leben NUR hier im lokalen State — nie im Query-/Mutation-Cache, nie persistiert.
  // `minted` = frisch erstellter Key (Einmal-Anzeige der Card); `keyInput` = der in die Befehle
  // eingesetzte Key (manuell eingebbar). Ein frischer Mint wandert automatisch ins Eingabefeld.
  const [minted, setMinted] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState("");
  function handleMinted(key: string | null) {
    setMinted(key);
    if (key) setKeyInput(key);
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <header>
          <h1 className="text-xl font-semibold">MCP &amp; API</h1>
          <p className="text-sm text-muted">
            API-Keys verwalten und Loumy als MCP-Server / Plugin in Claude Code, Codex & Co.
            einbinden.
          </p>
        </header>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">API-Keys</h2>
            <code className="truncate rounded-md border border-border bg-surface-2 px-2 py-1 font-mono text-xs text-muted">
              {GATEWAY_BASE}
            </code>
          </div>
          {status === "signed-in" ? (
            <ApiKeysCard minted={minted} setMinted={handleMinted} />
          ) : (
            <Card className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted">
                {status === "loading"
                  ? "…"
                  : "Zum Erstellen und Verwalten von API-Keys anmelden."}
              </p>
              {status !== "loading" && (
                <Button variant="primary" onClick={openAuth}>
                  Anmelden / Registrieren
                </Button>
              )}
            </Card>
          )}
        </section>

        <InstallSection keyInput={keyInput} setKeyInput={setKeyInput} />
        <CapabilitiesSection />

        <p className="text-xs text-muted">
          Konto, Credits & Pläne unter{" "}
          <Link to="/account" className="text-accent hover:underline">
            Account
          </Link>
          . Der private Plugin-/npx-Bezug (GitHub-Login nötig) wird beim Public-Gang auf npm
          umgestellt.
        </p>
      </div>
    </div>
  );
}
